import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Card, Form, Button, Alert, Row, Col, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axiosInstance from 'helpers/api';
import { toast } from 'react-hot-toast';
import {
  faUserTie,
  faBuilding,
  faIdCard,
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
  faLightbulb,
  faArrowRight,
  faArrowLeft,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

interface AgentSetupWizardProps {
  onComplete: () => void;
  onCancel?: () => void; // Add cancel prop to return to main screen
  userId?: string; // Add userId prop for API calls
}

interface VerifiedAgentData {
  agentMlsId: string;
  agentFullName: string;
  agentEmail: string;
  licensNumber: string;
  officeName: string;
  officeAddress?: string;
  verificationStatus: string;
  confidenceLevel?: string;
  companyName?: string;
  agentPhone?: string;
  active?: boolean;
  source?: string;
  internalAgentId?: string; // Add internal agent ID for analytics
  internalOfficeId?: string; // Add internal office ID for analytics
}

const AgentSetupWizard: React.FC<AgentSetupWizardProps> = ({ onComplete, onCancel, userId }) => {
  // Always start from step 1 when component mounts/remounts
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    agentMlsId: '',
    agentFullName: '',
    agentEmail: '',
    mlsName: 'ARMLS'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [verifiedAgentData, setVerifiedAgentData] = useState<VerifiedAgentData | null>(null);
  const [isAutoVerifying, setIsAutoVerifying] = useState(false);
  const [verificationTimeout, setVerificationTimeout] = useState<NodeJS.Timeout | null>(null);

  // Load existing agent data on component mount
  useEffect(() => {
    const loadExistingAgentData = async () => {
      if (!userId) return;

      try {
        console.log('🔍 Loading existing agent data for user:', userId);
        const response = await axiosInstance.get('onboarding/status');
        const data = response.data;

        if (data.success && data.data?.agent_analytics?.setup_complete && data.data?.agentInfo) {
          const agentInfo = data.data.agentInfo;
          console.log('✅ Found existing agent data:', agentInfo);

          // Pre-populate form with existing data
          setFormData({
            agentMlsId: agentInfo.mlsAgentId || '',
            agentFullName: agentInfo.agentFullName || '',
            agentEmail: agentInfo.agentEmail || '',
            mlsName: agentInfo.mlsName || 'ARMLS'
          });

          // Set verification status as success since data exists
          setVerificationStatus('success');
          
          // Create verified agent data object from existing info
          setVerifiedAgentData({
            agentMlsId: agentInfo.mlsAgentId || '',
            agentFullName: agentInfo.agentFullName || '',
            agentEmail: agentInfo.agentEmail || '',
            licensNumber: agentInfo.licensNumber || '',
            officeName: agentInfo.officeName || '',
            officeAddress: agentInfo.officeAddress || '',
            verificationStatus: agentInfo.verificationStatus || 'verified',
            confidenceLevel: 'high',
            companyName: agentInfo.officeName || '',
            agentPhone: agentInfo.agentPhone || '',
            active: true,
            source: 'existing_profile',
            internalAgentId: agentInfo.internalAgentId || '',
            internalOfficeId: agentInfo.internalOfficeId || ''
          });

          // If we have complete data, skip to step 3 (Analytics Setup)
          // Only do this if not in reconfiguration mode (onCancel means reconfigure)
          if (agentInfo.mlsAgentId && agentInfo.agentFullName && !onCancel) {
            console.log('📋 Complete agent data found, skipping to Analytics Setup');
            setCurrentStep(3);
          } else if (onCancel) {
            console.log('🔄 Reconfiguration mode - starting from step 1');
            setCurrentStep(1);
          }
        } else {
          console.log('ℹ️ No existing agent data found, starting fresh setup');
        }
      } catch (error) {
        console.warn('⚠️ Failed to load existing agent data:', error);
        // Don't show error to user, just proceed with empty form
      }
    };

    loadExistingAgentData();
  }, [userId, onCancel]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (verificationTimeout) {
        clearTimeout(verificationTimeout);
      }
    };
  }, [verificationTimeout]);

  const steps = useMemo(() => [
    {
      id: 1,
      title: 'Agent Identification',
      description: 'Enter your MLS agent information',
      icon: faUserTie
    },
    {
      id: 2,
      title: 'Verification',
      description: 'Verify your agent profile',
      icon: faIdCard
    },
    {
      id: 3,
      title: 'Analytics Setup',
      description: 'Initialize performance tracking',
      icon: faCheckCircle
    }
  ], []);

  // Real MLS Verification API call - wrapped in useCallback to prevent dependency issues
  const verifyAgentWithMLS = useCallback(async (agentMlsId: string): Promise<VerifiedAgentData | null> => {
    if (!userId) {
      throw new Error('User ID is required for agent verification');
    }

    const response = await axiosInstance.post('/onboarding/verify-agent', {
      userId: userId,
      agentMlsId: agentMlsId.trim()
    });

    const data = response.data;

    if (!data.success || !data.verified) {
      throw new Error(data.error || 'Agent not found in MLS system');
    }

    return data.data;
  }, [userId]);

  // Auto-verify when agent ID is entered (on blur or after short delay)
  const handleAgentIdVerification = useCallback(async (agentId: string) => {
    if (!agentId.trim() || agentId.length < 3) return;
    
    setIsAutoVerifying(true);
    setError(null);
    setVerificationStatus('verifying');
    console.log('🔍 Auto-verifying agent ID:', agentId);

    try {
      const agentData = await verifyAgentWithMLS(agentId);
      
      if (agentData) {
        console.log('✅ Agent verified:', agentData);
        // Auto-populate form with verified data
        setFormData(prev => ({
          ...prev,
          agentFullName: agentData.agentFullName || prev.agentFullName,
          agentEmail: agentData.agentEmail || prev.agentEmail
        }));
        setVerifiedAgentData(agentData);
        setVerificationStatus('success');
      }
    } catch (err: any) {
      console.warn('⚠️ Auto-verification failed:', err.message);
      // Don't show error for auto-verification, just reset state
      setVerifiedAgentData(null);
      setVerificationStatus('idle');
    } finally {
      setIsAutoVerifying(false);
    }
  }, [verifyAgentWithMLS]);

  const handleInputChange = useCallback((field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    
    // Auto-verify when agent MLS ID changes
    if (field === 'agentMlsId') {
      setVerifiedAgentData(null);
      setVerificationStatus('idle');
      
      // Clear existing timeout
      if (verificationTimeout) {
        clearTimeout(verificationTimeout);
      }
      
      // Set new timeout for verification
      const newTimeout = setTimeout(() => {
        handleAgentIdVerification(value);
      }, 1500); // Increased to 1.5 seconds for better UX
      
      setVerificationTimeout(newTimeout);
    }
  }, [handleAgentIdVerification, verificationTimeout]);

  const handleStepOne = useCallback(() => {
    if (!formData.agentMlsId.trim()) {
      setError('Agent MLS ID is required');
      return;
    }
    setCurrentStep(2);
  }, [formData.agentMlsId]);

  const handleVerification = async () => {
    setIsSubmitting(true);
    setVerificationStatus('verifying');
    setError(null);

    try {
      // If we don't have verified data yet, verify now
      if (!verifiedAgentData) {
        const agentData = await verifyAgentWithMLS(formData.agentMlsId);
        setVerifiedAgentData(agentData);
      }

      setVerificationStatus('success');
      setCurrentStep(3);
    } catch (err: any) {
      setVerificationStatus('error');
      setError(err.message || 'Verification failed. Please check your agent ID and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAnalyticsSetup = async () => {
    if (!userId) {
      setError('User ID is required for setup');
      return;
    }

    setIsSubmitting(true);
    try {
      // Call the enhanced agent setup endpoint with verified data
      const response = await axiosInstance.post('/onboarding/agent-setup', {
        agentMlsId: formData.agentMlsId,
        agentFullName: formData.agentFullName,
        agentEmail: formData.agentEmail,
        mlsName: formData.mlsName
      });

      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Setup failed');
      }

      // Log the enhanced success details
      console.log('🎉 Agent Analytics Setup Complete!', {
        message: data.message,
        agentProfile: data.data?.agentProfile,
        verification: data.data?.verification,
        nextSteps: data.nextSteps
      });

      // Show enhanced success toast
      if (data.data?.agentProfile) {
        const profile = data.data.agentProfile;
        toast.success(
          `🎉 Setup Complete!\n\n✅ ${profile.agentFullName} (${profile.agentMlsId})\n✅ ${profile.officeName || 'Your Real Estate Office'}\n✅ Analytics tracking activated\n\nReady to view your performance insights!`,
          {
            duration: 6000,
            style: {
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontWeight: '500',
              borderRadius: '12px',
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)',
              maxWidth: '500px'
            },
            icon: '🎉'
          }
        );
      } else {
        toast.success('Agent Analytics Setup Complete! 🎉');
      }

      // Store the setup success data for display
      if (data.data?.agentProfile) {
        // Update form data with final saved information
        setFormData(prev => ({
          ...prev,
          agentFullName: data.data.agentProfile.agentFullName || prev.agentFullName,
          agentEmail: data.data.agentProfile.agentEmail || prev.agentEmail,
          mlsName: data.data.agentProfile.mlsName || prev.mlsName
        }));

        // Update verified agent data with database info
        setVerifiedAgentData(prev => prev ? {
          ...prev,
          databaseId: data.data.agentProfile.id,
          savedAt: data.data.agentProfile.savedAt,
          setupComplete: true
        } : null);
      }

      // Mark agent analytics as complete in localStorage (for persistent state)
      localStorage.setItem('agentAnalyticsComplete', 'true');
      
      // Success! Complete the onboarding with enhanced feedback
      onComplete();
    } catch (err: any) {
      console.error('❌ Agent setup error:', err);
      
      // Show enhanced error toast
      const errorMessage = err.message || 'Analytics setup failed. You can try again later from the dashboard.';
      toast.error(
        `❌ Setup Error\n\n${errorMessage}\n\nDon't worry - you can retry from Profile Settings later.`,
        {
          duration: 5000,
          style: {
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            fontWeight: '500',
            borderRadius: '12px',
            boxShadow: '0 0 30px rgba(239, 68, 68, 0.3)',
            maxWidth: '500px'
          },
          icon: '⚠️'
        }
      );
      
      setError(errorMessage);
      // Still complete since verification is done
      setTimeout(() => onComplete(), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add verification status indicator
  const getVerificationStatusMessage = useCallback(() => {
    if (isAutoVerifying) {
      return (
        <div className="d-flex align-items-center text-info mt-2">
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
          <span>Connecting to ARMLS system...</span>
        </div>
      );
    }
    
    if (verifiedAgentData?.source === 'real_mls_api') {
      return (
        <div className="d-flex align-items-center text-success mt-2">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <span>Connected to live MLS data</span>
        </div>
      );
    }
    
    if (verifiedAgentData?.source === 'mock_fallback') {
      return (
        <div className="d-flex align-items-center text-warning mt-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          <span>Using fallback data - MLS system unavailable</span>
        </div>
      );
    }
    
    return null;
  }, [isAutoVerifying, verifiedAgentData]);

  const stepIndicatorElement = useMemo(() => (
    <div className="mb-4">
      <Row className="align-items-center">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <Col xs="auto" className="text-center">
              <div 
                className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                  currentStep >= step.id 
                    ? 'bg-primary text-white' 
                    : 'bg-light text-muted border'
                }`}
                style={{ width: '48px', height: '48px' }}
              >
                <FontAwesomeIcon icon={step.icon} />
              </div>
              <div className="text-center">
                <small className="fw-bold d-block">{step.title}</small>
                <small className="text-muted">{step.description}</small>
              </div>
            </Col>
            {index < steps.length - 1 && (
              <Col className="text-center">
                <div 
                  style={{ 
                    height: '2px', 
                    background: currentStep > step.id ? '#0d6efd' : '#dee2e6',
                    transition: 'background 0.3s ease'
                  }} 
                />
              </Col>
            )}
          </React.Fragment>
        ))}
      </Row>
    </div>
  ), [steps, currentStep]);

  const StepOneContent = (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-primary text-white">
        <FontAwesomeIcon icon={faUserTie} className="me-2" />
        Step 1: Agent Information
      </Card.Header>
      <Card.Body className="p-4">
        {/* Show notification when existing data is loaded */}
        {verifiedAgentData?.source === 'existing_profile' && (
          <Alert variant="success" className="d-flex align-items-start mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 mt-1" />
            <div>
              <strong>Existing Profile Found!</strong><br />
              We've pre-populated your form with your existing agent information. 
              You can review and update the details below, then proceed to complete your analytics setup.
            </div>
          </Alert>
        )}

        <Alert variant="info" className="d-flex align-items-start mb-4">
          <FontAwesomeIcon icon={faLightbulb} className="me-2 mt-1" />
          <div>
            <strong>Quick Setup</strong><br />
            Enter your MLS agent information to connect your performance data. 
            You can find your Agent ID in your MLS system under your profile.
          </div>
        </Alert>

        <Row className="g-4">
          <Col lg={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                <FontAwesomeIcon icon={faIdCard} className="me-2 text-primary" />
                MLS Agent ID *
              </Form.Label>
              <div className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="e.g., mb678"
                  value={formData.agentMlsId}
                  onChange={(e) => handleInputChange('agentMlsId', e.target.value)}
                  style={{ 
                    borderRadius: '8px', 
                    padding: '12px',
                    ...(verifiedAgentData ? {
                      borderColor: '#00d4ff',
                      boxShadow: '0 0 0 0.2rem rgba(0, 212, 255, 0.15)'
                    } : isAutoVerifying ? {
                      borderColor: '#3b82f6',
                      boxShadow: '0 0 0 0.2rem rgba(59, 130, 246, 0.15)'
                    } : {})
                  }}
                />
                {getVerificationStatusMessage()}
              </div>
            </Form.Group>
          </Col>
          
          <Col lg={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                <FontAwesomeIcon icon={faBuilding} className="me-2 text-info" />
                MLS System
              </Form.Label>
              <Form.Select
                value={formData.mlsName}
                onChange={(e) => handleInputChange('mlsName', e.target.value)}
                style={{ borderRadius: '8px', padding: '12px' }}
              >
                <option value="ARMLS">Arizona Regional MLS (ARMLS)</option>
                <option value="OTHER">Other MLS System</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                Agent Full Name
                {verifiedAgentData && (
                  <span 
                    className="ms-2 d-inline-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontSize: '11px'
                    }}
                  >
                    <span className="fw-medium text-success">Auto-filled</span>
                  </span>
                )}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Your full name"
                value={formData.agentFullName}
                onChange={(e) => handleInputChange('agentFullName', e.target.value)}
                style={{ 
                  borderRadius: '8px', 
                  padding: '12px',
                  ...(verifiedAgentData?.agentFullName ? {
                    borderColor: '#00d4ff',
                    boxShadow: '0 0 0 0.2rem rgba(0, 212, 255, 0.15)'
                  } : {})
                }}
              />
              <Form.Text className="text-muted">
                {verifiedAgentData ? 'Auto-filled from MLS verification' : 'Optional: Helps with verification'}
              </Form.Text>
            </Form.Group>
          </Col>

          <Col lg={6}>
            <Form.Group>
              <Form.Label className="fw-semibold">
                Email Address
                {verifiedAgentData && (
                  <span 
                    className="ms-2 d-inline-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontSize: '11px'
                    }}
                  >
                    <span className="fw-medium text-success">Auto-filled</span>
                  </span>
                )}
              </Form.Label>
              <Form.Control
                type="email"
                placeholder="your.email@example.com"
                value={formData.agentEmail}
                onChange={(e) => handleInputChange('agentEmail', e.target.value)}
                style={{ 
                  borderRadius: '8px', 
                  padding: '12px',
                  ...(verifiedAgentData?.agentEmail ? {
                    borderColor: '#00d4ff',
                    boxShadow: '0 0 0 0.2rem rgba(0, 212, 255, 0.15)'
                  } : {})
                }}
              />
              <Form.Text className="text-muted">
                {verifiedAgentData ? 'Auto-filled from MLS verification' : 'Optional: For notifications and updates'}
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>

        {verifiedAgentData && (
          <div 
            className="mt-4 p-4"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              borderRadius: '16px'
            }}
          >
            <Row className="g-3">
              <Col md={12} className="mb-2">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    <div 
                      className="rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ 
                        width: "40px", 
                        height: "40px",
                        background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                        boxShadow: '0 0 15px rgba(0, 212, 255, 0.3)'
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={faCheckCircle} 
                        className="text-white" 
                        style={{ filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                      />
                    </div>
                    <div>
                      <h6 className="mb-0" style={{ 
                        color: '#1f2937',
                        textShadow: '0 0 10px rgba(0, 212, 255, 0.2)',
                        fontWeight: 600
                      }}>
                        MLS Verification Complete
                      </h6>
                      <small className="text-muted">Agent profile successfully verified</small>
                    </div>
                  </div>
                  {verifiedAgentData.confidenceLevel && (
                    <div style={{
                      background: verifiedAgentData.confidenceLevel === 'High' 
                        ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
                        : 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: verifiedAgentData.confidenceLevel === 'High' 
                        ? '1px solid rgba(16, 185, 129, 0.3)'
                        : '1px solid rgba(245, 158, 11, 0.3)'
                    }}>
                      <span 
                        className={`fw-semibold ${verifiedAgentData.confidenceLevel === 'High' ? 'text-success' : 'text-warning'}`}
                        style={{ 
                          fontSize: '12px', 
                          textTransform: 'uppercase', 
                          letterSpacing: '0.5px'
                        }}
                      >
                        {verifiedAgentData.confidenceLevel} Confidence
                      </span>
                    </div>
                  )}
                </div>
              </Col>
              
              {/* Professional Information */}
              <Col md={6}>
                <div 
                  className="p-3"
                  style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <div className="mb-2" style={{ fontSize: '14px' }}>
                    <strong style={{ color: '#374151' }}>Office:</strong> 
                    <span className="ms-2 text-muted">{verifiedAgentData.officeName || 'Not available'}</span>
                  </div>
                  {verifiedAgentData.officeAddress && (
                    <div className="mb-2" style={{ fontSize: '14px' }}>
                      <strong style={{ color: '#374151' }}>Address:</strong> 
                      <span className="ms-2 text-muted">{verifiedAgentData.officeAddress}</span>
                    </div>
                  )}
                  {verifiedAgentData.companyName && (
                    <div className="mb-2" style={{ fontSize: '14px' }}>
                      <strong style={{ color: '#374151' }}>Company:</strong> 
                      <span className="ms-2 text-muted">{verifiedAgentData.companyName}</span>
                    </div>
                  )}
                  <div className="mb-0" style={{ fontSize: '14px' }}>
                    <strong style={{ color: '#374151' }}>License:</strong> 
                    <span className="ms-2 text-muted">{verifiedAgentData.licensNumber || 'Not available'}</span>
                  </div>
                </div>
              </Col>
              
              <Col md={6}>
                <div 
                  className="p-3"
                  style={{
                    background: 'rgba(255, 255, 255, 0.02)',
                    borderRadius: '12px',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                  }}
                >
                  {verifiedAgentData.agentPhone && (
                    <div className="mb-2" style={{ fontSize: '14px' }}>
                      <strong style={{ color: '#374151' }}>Phone:</strong> 
                      <span className="ms-2 text-muted">{verifiedAgentData.agentPhone}</span>
                    </div>
                  )}
                  <div className="mb-2 d-flex align-items-center" style={{ fontSize: '14px' }}>
                    <strong style={{ color: '#374151' }}>MLS Status:</strong> 
                    <div className="ms-2">
                      <div 
                        className="d-inline-block"
                        style={{
                          background: verifiedAgentData.active 
                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)'
                            : 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          border: verifiedAgentData.active 
                            ? '1px solid rgba(16, 185, 129, 0.3)'
                            : '1px solid rgba(239, 68, 68, 0.3)'
                        }}
                      >
                        <span 
                          className={`fw-medium ${verifiedAgentData.active ? 'text-success' : 'text-danger'}`}
                          style={{ fontSize: '12px' }}
                        >
                          {verifiedAgentData.active ? 'Active Member' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mb-0 d-flex align-items-center" style={{ fontSize: '14px' }}>
                    <strong style={{ color: '#374151' }}>Verification:</strong> 
                    <div className="ms-2">
                      <div 
                        className="d-inline-block"
                        style={{
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.15) 100%)',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                      >
                        <span 
                          className="fw-medium text-info"
                          style={{ fontSize: '12px' }}
                        >
                          {verifiedAgentData.verificationStatus}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>

              {/* Data Source and Timestamp */}
              <Col md={12}>
                <div 
                  className="pt-3 mt-2"
                  style={{ 
                    borderTop: '1px solid rgba(0, 0, 0, 0.08)'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="me-2" style={{ color: '#00d4ff' }} />
                      <small style={{ color: '#6b7280', fontSize: '13px' }}>
                        Data source: <span className="fw-medium">
                          {verifiedAgentData.source === 'real_mls_api' ? 'Live ARMLS System' : 'Mock Data'}
                        </span>
                      </small>
                    </div>
                    <small style={{ color: '#9ca3af', fontSize: '12px' }}>
                      Verified: {new Date().toLocaleTimeString()}
                    </small>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {error && (
          <Alert variant="danger" className="mt-3">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-between mt-4">
          {onCancel && (
            <Button 
              variant="outline-secondary" 
              onClick={onCancel}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Cancel Setup
            </Button>
          )}
          <Button 
            variant="primary" 
            onClick={handleStepOne}
            className="px-4"
            style={{ marginLeft: onCancel ? 'auto' : '0' }}
          >
            Continue <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const StepTwoContent = (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-info text-white">
        <FontAwesomeIcon icon={faIdCard} className="me-2" />
        Step 2: Verification
      </Card.Header>
      <Card.Body className="p-4">
        <div className="text-center mb-4">
          <h5>Verify Agent Information</h5>
          <p className="text-muted">
            {verifiedAgentData 
              ? 'Your agent profile has been pre-verified. Click below to confirm and proceed.'
              : 'We\'ll connect to the MLS system to verify your agent profile and setup analytics.'
            }
          </p>
        </div>

        <div 
          className={`border-0 mb-4 p-4`}
          style={{
            background: verifiedAgentData 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
              : 'rgba(248, 249, 250, 0.8)',
            backdropFilter: verifiedAgentData ? 'blur(15px)' : 'none',
            border: verifiedAgentData ? '1px solid rgba(0, 212, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '16px'
          }}
        >
          <div className="p-3">
            <Row>
              <Col sm={6}>
                <strong style={{ color: '#374151' }}>Agent MLS ID:</strong> 
                <span className="ms-2">{formData.agentMlsId}</span>
                {verifiedAgentData && (
                  <span 
                    className="ms-2 d-inline-flex align-items-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                      padding: '4px 8px',
                      borderRadius: '8px',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      fontSize: '11px'
                    }}
                  >
                    <span className="fw-medium text-success">Verified</span>
                  </span>
                )}
              </Col>
              {verifiedAgentData?.internalAgentId && (
                <Col sm={12} className="mt-2">
                  <strong style={{ color: '#374151' }}>Analytics Agent ID:</strong> 
                  <span className="ms-2 text-muted font-monospace" style={{ fontSize: '14px' }}>
                    {verifiedAgentData.internalAgentId}
                  </span>
                  <small className="ms-2 text-info">
                    (Required for analytics reports)
                  </small>
                </Col>
              )}

              {verifiedAgentData?.internalOfficeId && (
                <Col sm={12} className="mt-1">
                  <strong style={{ color: '#374151' }}>Analytics Office ID:</strong> 
                  <span className="ms-2 text-muted font-monospace" style={{ fontSize: '14px' }}>
                    {verifiedAgentData.internalOfficeId}
                  </span>
                  <small className="ms-2 text-info">
                    (For office-level analytics)
                  </small>
                </Col>
              )}
              <Col sm={6}>
                <strong style={{ color: '#374151' }}>MLS System:</strong> 
                <span className="ms-2">{formData.mlsName}</span>
              </Col>
              {formData.agentFullName && (
                <Col sm={6} className="mt-2">
                  <strong style={{ color: '#374151' }}>Name:</strong> 
                  <span className="ms-2">{formData.agentFullName}</span>
                  {verifiedAgentData && verifiedAgentData.agentFullName === formData.agentFullName && (
                    <span 
                      className="ms-2 d-inline-flex align-items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontSize: '10px'
                      }}
                    >
                      <span className="fw-medium text-success">✓</span>
                    </span>
                  )}
                </Col>
              )}
              {formData.agentEmail && (
                <Col sm={6} className="mt-2">
                  <strong style={{ color: '#374151' }}>Email:</strong> 
                  <span className="ms-2">{formData.agentEmail}</span>
                  {verifiedAgentData && verifiedAgentData.agentEmail === formData.agentEmail && (
                    <span 
                      className="ms-2 d-inline-flex align-items-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                        padding: '2px 6px',
                        borderRadius: '6px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontSize: '10px'
                      }}
                    >
                      <span className="fw-medium text-success">✓</span>
                    </span>
                  )}
                </Col>
              )}
              {verifiedAgentData && (
                <>
                  <Col sm={6} className="mt-2">
                    <strong style={{ color: '#374151' }}>Office:</strong> 
                    <span className="ms-2 text-muted">{verifiedAgentData.officeName}</span>
                  </Col>
                  <Col sm={6} className="mt-2">
                    <strong style={{ color: '#374151' }}>License:</strong> 
                    <span className="ms-2 text-muted">{verifiedAgentData.licensNumber}</span>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>

        {verifiedAgentData && (
          <div 
            className="mb-4 p-3"
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px'
            }}
          >
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-3"
                style={{ 
                  width: "32px", 
                  height: "32px",
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 0 15px rgba(16, 185, 129, 0.3)'
                }}
              >
                <FontAwesomeIcon 
                  icon={faCheckCircle} 
                  className="text-white" 
                  style={{ fontSize: '14px', filter: 'drop-shadow(0 0 4px rgba(255, 255, 255, 0.5))' }}
                />
              </div>
              <div>
                <div className="fw-semibold text-success mb-1">Pre-verification Complete!</div>
                <small className="text-muted">Your agent profile has been verified successfully.</small>
              </div>
            </div>
          </div>
        )}

        {verifiedAgentData?.internalAgentId && (
          <Col sm={12} className="mt-3">
            <Alert variant="success" className="mb-0 py-2">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              <strong>Analytics Setup Complete!</strong> Your internal agent ID has been verified and you're ready for performance tracking.
            </Alert>
          </Col>
        )}

        {verificationStatus === 'verifying' && (
          <Alert variant="info" className="text-center">
            <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
            Verifying agent information with MLS system...
          </Alert>
        )}

        {verificationStatus === 'error' && error && (
          <Alert variant="danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {error}
          </Alert>
        )}

        <div className="d-flex justify-content-between">
          <div className="d-flex gap-2">
            {onCancel && (
              <Button 
                variant="outline-secondary" 
                onClick={onCancel}
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Cancel
              </Button>
            )}
            <Button 
              variant="outline-secondary" 
              onClick={() => setCurrentStep(1)}
              disabled={isSubmitting}
            >
              Back to Step 1
            </Button>
          </div>
          <Button 
            variant="info" 
            onClick={handleVerification}
            disabled={isSubmitting || verificationStatus === 'verifying'}
            className="px-4"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Verifying...
              </>
            ) : verifiedAgentData ? (
              <>
                Confirm & Setup
                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </>
            ) : (
              <>
                Verify & Setup
                <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
              </>
            )}
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  const StepThreeContent = (
    <Card className="border-0 shadow-sm">
      <Card.Header 
        className="text-white border-0"
        style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
        Step 3: Analytics Setup
      </Card.Header>
      <Card.Body className="p-4 text-center">
        <div className="mb-4">
          <div 
            className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3"
            style={{ 
              width: "80px", 
              height: "80px",
              background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
              boxShadow: '0 0 30px rgba(0, 212, 255, 0.4)'
            }}
          >
            <FontAwesomeIcon 
              icon={faCheckCircle} 
              className="text-white" 
              style={{ fontSize: '32px', filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.5))' }}
            />
          </div>
          <h4 style={{ 
            color: '#1f2937',
            textShadow: '0 0 10px rgba(0, 212, 255, 0.2)',
            fontWeight: 600
          }}>
            {verifiedAgentData?.source === 'existing_profile' ? 'Profile Updated!' : 'Verification Successful!'}
          </h4>
          <p className="text-muted">
            {verifiedAgentData?.source === 'existing_profile' 
              ? 'Your existing agent profile has been loaded. Now let\'s ensure your analytics are properly configured.'
              : 'Your agent profile has been connected to the MLS system. Now let\'s initialize your analytics dashboard.'
            }
          </p>
        </div>

        {verifiedAgentData && (
          <div 
            className="mb-4 p-3"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(15px)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              borderRadius: '16px'
            }}
          >
            <div className="d-flex align-items-center justify-content-center mb-2">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center me-2"
                style={{ 
                  width: "24px", 
                  height: "24px",
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                }}
              >
                <span className="text-white fw-bold" style={{ fontSize: '12px' }}>✓</span>
              </div>
              <span className="fw-medium" style={{ color: '#374151' }}>
                Agent profile verified: {verifiedAgentData.agentFullName} ({verifiedAgentData.agentMlsId})
              </span>
            </div>
            <div className="small text-muted">
              Office: {verifiedAgentData.officeName} | License: {verifiedAgentData.licensNumber}
              {verifiedAgentData.internalAgentId && (
                <>
                  <br />
                  <strong className="text-primary">Analytics ID:</strong> {verifiedAgentData.internalAgentId}
                </>
              )}
            </div>
          </div>
        )}

        <p className="mb-4 text-muted">
          We'll now calculate your initial analytics data and set up performance tracking. 
          This includes analyzing your transaction history and establishing baseline metrics.
        </p>

        <Button 
          size="lg" 
          onClick={handleAnalyticsSetup}
          disabled={isSubmitting}
          className="px-5 border-0"
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 0 20px rgba(16, 185, 129, 0.3)',
            padding: '12px 32px',
            fontWeight: 600
          }}
        >
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Setting up analytics...
            </>
          ) : (
            <>
              Complete Setup
              <FontAwesomeIcon icon={faCheckCircle} className="ms-2" />
            </>
          )}
        </Button>

        {error && (
          <div 
            className="mt-4 p-3"
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px'
            }}
          >
            <div className="d-flex align-items-center justify-content-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
              <span className="text-warning">{error}</span>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <div className="agent-setup-wizard">
      <Card className="border-0 shadow">
        <Card.Header className="text-center bg-white border-bottom">
          <div className="d-flex align-items-center justify-content-between mb-3">
            {onCancel && (
              <Button 
                variant="outline-secondary" 
                size="sm"
                onClick={onCancel}
                className="d-flex align-items-center"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Analytics
              </Button>
            )}
            <h4 className="mb-0 flex-grow-1">Agent Analytics Setup</h4>
            <div style={{ width: onCancel ? 'auto' : '0' }}></div> {/* Spacer for centering */}
          </div>
          <ProgressBar 
            now={(currentStep / steps.length) * 100} 
            variant="primary" 
            className="mb-3"
            style={{ height: '8px' }}
          />
          {stepIndicatorElement}
        </Card.Header>
        <Card.Body className="p-0">
          {currentStep === 1 && StepOneContent}
          {currentStep === 2 && StepTwoContent}
          {currentStep === 3 && StepThreeContent}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AgentSetupWizard; 